import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function TiersIcon() {
  return <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" data-id="element-256">
      <rect x="4" y="20" width="24" height="6" rx="2" stroke="#FF6B6B" strokeWidth="2" data-id="element-257" />
      <rect x="7" y="12" width="18" height="6" rx="2" stroke="#FF8A5C" strokeWidth="2" data-id="element-258" />
      <rect x="10" y="4" width="12" height="6" rx="2" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.2" data-id="element-259" />
    </svg>;
}
function PathIcon() {
  return <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" data-id="element-260">
      <path d="M6 26C6 26 10 18 16 18C22 18 16 10 22 6" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" fill="none" data-id="element-261" />
      <circle cx="6" cy="26" r="3" fill="#FF8A5C" data-id="element-262" />
      <circle cx="22" cy="6" r="3" fill="#FF6B6B" data-id="element-263" />
      <circle cx="16" cy="18" r="2" fill="#FF6B6B" fillOpacity="0.5" data-id="element-264" />
    </svg>;
}
function VipIcon() {
  return <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" data-id="element-265">
      <polygon points="16,3 20,12 30,12 22,19 25,29 16,23 7,29 10,19 2,12 12,12" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.15" data-id="element-266" />
    </svg>;
}
function TriggerIcon() {
  return <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" data-id="element-267">
      <path d="M18 3L8 18h8l-2 11 10-15h-8l2-11z" stroke="#FF6B6B" strokeWidth="2" fill="#FF8A5C" fillOpacity="0.2" data-id="element-268" />
    </svg>;
}
interface ValuePropProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}
function ValueProp({
  title,
  description,
  icon
}: ValuePropProps) {
  return <motion.div variants={cardVariants} className="bg-navy-light/50 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/5" data-id="element-269">
      <div className="w-14 h-14 rounded-xl bg-coral/10 flex items-center justify-center mb-5" data-id="element-270">
        {icon}
      </div>
      <h3 className="font-display font-bold text-base md:text-lg text-white mb-3" data-id="element-271">
        {title}
      </h3>
      <p className="font-body text-slate-400 text-sm md:text-base leading-relaxed" data-id="element-272">
        {description}
      </p>
    </motion.div>;
}
interface ProgressRingProps {
  value: string;
  subtitle: string;
  percentage: number;
}
function ProgressRing({
  value,
  subtitle,
  percentage
}: ProgressRingProps) {
  const [inView, setInView] = useState(false);
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const targetOffset = circumference - percentage / 100 * circumference;
  return <motion.div className="flex flex-col items-center" initial={{
    opacity: 0,
    scale: 0.8
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }} onViewportEnter={() => setInView(true)} data-id="element-273">
      <div className="relative w-[148px] h-[148px]" data-id="element-274">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 140 140" aria-hidden="true" data-id="element-275">
          <defs data-id="element-276">
            <linearGradient id={`ringGrad-${percentage}`} x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-277">
              <stop offset="0%" stopColor="#FF6B6B" data-id="element-278" />
              <stop offset="100%" stopColor="#FF8A5C" data-id="element-279" />
            </linearGradient>
          </defs>
          {/* Track */}
          <circle cx="70" cy="70" r={radius} fill="none" stroke="#1B2A4A" strokeWidth="8" data-id="element-280" />
          {/* Progress */}
          <circle cx="70" cy="70" r={radius} fill="none" stroke={`url(#ringGrad-${percentage})`} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={inView ? targetOffset : circumference} className={inView ? 'progress-ring-animated' : ''} style={{
          '--circumference': circumference,
          '--target-offset': targetOffset,
          transition: inView ? 'none' : undefined
        } as React.CSSProperties} data-id="element-281" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-282">
          <span className="font-display font-bold text-2xl md:text-3xl text-coral" data-id="element-283">
            {value}
          </span>
        </div>
      </div>
      <p className="font-body text-slate-300 text-sm mt-3 text-center" data-id="element-284">
        {subtitle}
      </p>
    </motion.div>;
}
export function Solution() {
  return <section id="solution" className="w-full bg-navy py-20 md:py-28 px-6" data-id="element-285">
      <div className="max-w-6xl mx-auto" data-id="element-286">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-287">
          <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-4" data-id="element-288">
            Your AI-Powered Loyalty Command Center
          </h2>
          <p className="font-body text-slate-400 text-base md:text-lg max-w-2xl mx-auto" data-id="element-289">
            A neural engine that learns, adapts, and optimizes every player's
            journey in real time.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} data-id="element-290">
          <ValueProp title="Dynamic Loyalty Tiers" description="AI continuously recalibrates tier boundaries based on player behavior clusters — no more static bronze-silver-gold." icon={<TiersIcon data-id="element-292" />} data-id="element-291" />
          <ValueProp title="Personalized Reward Paths" description="Each player gets a unique progression journey tailored to their play style, preferences, and engagement patterns." icon={<PathIcon data-id="element-294" />} data-id="element-293" />
          <ValueProp title="Predictive VIP Scoring" description="Identify tomorrow's whales today with neural behavioral pattern recognition that sees potential before it surfaces." icon={<VipIcon data-id="element-296" />} data-id="element-295" />
          <ValueProp title="Real-Time Engagement Triggers" description="Automated micro-interventions fired at the exact moment of maximum impact — turning hesitation into action." icon={<TriggerIcon data-id="element-298" />} data-id="element-297" />
        </motion.div>

        {/* Progress Rings */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-10 md:gap-16" data-id="element-299">
          <ProgressRing value="3.2x" subtitle="Player Retention" percentage={80} data-id="element-300" />
          <ProgressRing value="47%" subtitle="Increase in LTV" percentage={65} data-id="element-301" />
          <ProgressRing value="89%" subtitle="VIP Satisfaction" percentage={89} data-id="element-302" />
        </div>
      </div>
    </section>;
}